import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AddCustomerDialog from "../components/AddCustomerDialog";
import ListPaginationBar from "../components/ListPaginationBar";
import {
  DEFAULT_PAGE_SIZE,
  parsePaginatedResponse,
} from "../constants/pagination";
import { customersAPI } from "../services/api";
import { addCustomer } from "../store/slices/referenceDataSlice";
import { formatDate } from "../utils/dateFormatter";
import { useRequirePermission } from "../hooks/useHasPermission";
import { PERMISSIONS } from "../utils/permissions";

const CustomersPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const hasAccess = useRequirePermission(
    PERMISSIONS.MANAGE_CUSTOMERS,
    onNavigate
  );
  // Check if current language is RTL
  const isRTL = i18n.language === "ar";

  // State for managing customers
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dialog states
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    gender: "",
    birthdate: "",
    contact: "",
    address: "",
    note: "",
  });
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const genderChoices = [
    { value: "male", label: "ذكر" },
    { value: "female", label: "أنثى" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadCustomers = useCallback(
    async (overrides = {}) => {
      const currentPage = overrides.page ?? page;
      const currentSize = overrides.page_size ?? pageSize;
      setIsLoading(true);
      setError(null);
      try {
        const params = { page: currentPage, page_size: currentSize };
        if (debouncedSearch) {
          params.search = debouncedSearch;
        }
        const response = await customersAPI.getCustomers(params);
        const { items, totalCount: count } = parsePaginatedResponse(response);
        setCustomers(items);
        setTotalCount(count);
      } catch (err) {
        console.error("Error loading customers:", err);
        setError("فشل في تحميل قائمة العملاء");
      } finally {
        setIsLoading(false);
      }
    },
    [page, pageSize, debouncedSearch]
  );

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleAddCustomer = () => {
    setAddCustomerDialogOpen(true);
  };

  const handleCustomerAdded = (newCustomer) => {
    dispatch(addCustomer(newCustomer));
    setPage(1);
    loadCustomers({ page: 1 });
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditFormData({
      name: customer.name || "",
      email: customer.email || "",
      gender: customer.gender || "",
      birthdate: customer.birthdate || "",
      contact: customer.contact || "",
      address: customer.address || "",
      note: customer.note || "",
    });
    setEditErrors({});
    setEditCustomerDialogOpen(true);
  };

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer);
    setDeleteConfirmDialogOpen(true);
  };

  const handleEditInputChange = (field) => (event) => {
    const value = event.target.value;
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (editErrors[field]) {
      setEditErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};

    if (!editFormData.name.trim()) {
      newErrors.name = "اسم العميل مطلوب";
    }

    if (!editFormData.contact.trim()) {
      newErrors.contact = "معلومات التواصل مطلوبة";
    }

    if (editFormData.email && editFormData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editFormData.email)) {
        newErrors.email = "البريد الإلكتروني غير صحيح";
      }
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateCustomer = async () => {
    if (!validateEditForm() || !selectedCustomer) {
      return;
    }

    setIsSubmitting(true);
    setEditErrors({});

    try {
      const customerData = {
        name: editFormData.name.trim(),
        contact: editFormData.contact.trim(),
        email: editFormData.email.trim() || null,
        gender: editFormData.gender || null,
        birthdate: editFormData.birthdate || null,
        address: editFormData.address.trim() || null,
        note: editFormData.note.trim() || null,
      };

      const updatedCustomer = await customersAPI.updateCustomer(
        selectedCustomer.customer_id,
        customerData
      );

      setEditCustomerDialogOpen(false);
      await loadCustomers();
      setSelectedCustomer(null);
    } catch (error) {
      console.error("Error updating customer:", error);
      setEditErrors({ general: error.message || "حدث خطأ في تحديث العميل" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCustomer) return;

    setIsSubmitting(true);
    try {
      await customersAPI.deleteCustomer(selectedCustomer.customer_id);

      setDeleteConfirmDialogOpen(false);
      await loadCustomers();
      setSelectedCustomer(null);
    } catch (error) {
      console.error("Error deleting customer:", error);
      setError(error.message || "فشل في حذف العميل");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGenderLabel = (gender) => {
    const choice = genderChoices.find((c) => c.value === gender);
    return choice ? choice.label : "-";
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <Box
      dir={isRTL ? "rtl" : "ltr"}
      sx={{
        p: { xs: 0.5, sm: 1, md: 1.5 },
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: "100%",
          width: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
              fontWeight: "bold",
            }}
          >
            إدارة العملاء
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCustomer}
            size="large"
            sx={{
              minWidth: "160px",
              height: "48px",
              fontSize: "16px",
            }}
          >
            إضافة عميل جديد
          </Button>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="البحث في العملاء..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mx: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: { xs: "16px", sm: "16px" },
                minHeight: { xs: "56px", sm: "56px" },
              },
            }}
          />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Customers Table */}
        {!isLoading && (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    الاسم
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    التواصل
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    البريد الإلكتروني
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    الجنس
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    تاريخ الميلاد
                  </TableCell>
                  <TableCell align="left" sx={{ fontWeight: "bold" }}>
                    العنوان
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    الإجراءات
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm ? "لا توجد نتائج للبحث" : "لا يوجد عملاء"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer.customer_id} hover>
                      <TableCell align="left">
                        <Typography variant="body2" fontWeight="medium">
                          {customer.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        {customer.contact}
                      </TableCell>
                      <TableCell align="left">
                        {customer.email || "-"}
                      </TableCell>
                      <TableCell align="left">
                        {getGenderLabel(customer.gender)}
                      </TableCell>
                      <TableCell align="left">
                        {formatDate(customer.birthdate)}
                      </TableCell>
                      <TableCell align="left">
                        {customer.address ? (
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {customer.address}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditCustomer(customer)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCustomer(customer)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && (
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isRTL={isRTL}
          />
        )}
      </Paper>

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Edit Customer Dialog */}
      <Dialog
        open={editCustomerDialogOpen}
        onClose={() => setEditCustomerDialogOpen(false)}
        maxWidth="md"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6" component="div">
              تعديل بيانات العميل
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {editErrors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {editErrors.general}
            </Alert>
          )}

          <Box component="form" sx={{ mt: 1 }}>
            {/* Name and Contact */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 3,
              }}
            >
              <TextField
                fullWidth
                label="اسم العميل *"
                value={editFormData.name}
                onChange={handleEditInputChange("name")}
                error={!!editErrors.name}
                helperText={editErrors.name}
                required
                variant="outlined"
              />

              <TextField
                fullWidth
                label="معلومات التواصل *"
                value={editFormData.contact}
                onChange={handleEditInputChange("contact")}
                error={!!editErrors.contact}
                helperText={editErrors.contact}
                required
                variant="outlined"
              />
            </Box>

            {/* Email and Gender */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 3,
              }}
            >
              <TextField
                fullWidth
                label="البريد الإلكتروني"
                type="email"
                value={editFormData.email}
                onChange={handleEditInputChange("email")}
                error={!!editErrors.email}
                helperText={editErrors.email}
                variant="outlined"
              />

              <FormControl fullWidth>
                <InputLabel>الجنس</InputLabel>
                <Select
                  value={editFormData.gender}
                  onChange={handleEditInputChange("gender")}
                  label="الجنس"
                >
                  <MenuItem value="">
                    <em>اختر الجنس</em>
                  </MenuItem>
                  {genderChoices.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Birthdate */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="تاريخ الميلاد"
                type="date"
                value={editFormData.birthdate}
                onChange={handleEditInputChange("birthdate")}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
              />
            </Box>

            {/* Address */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="العنوان"
                multiline
                rows={2}
                value={editFormData.address}
                onChange={handleEditInputChange("address")}
                variant="outlined"
              />
            </Box>

            {/* Note */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={2}
                value={editFormData.note}
                onChange={handleEditInputChange("note")}
                variant="outlined"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setEditCustomerDialogOpen(false)}
            disabled={isSubmitting}
            size="large"
          >
            إلغاء
          </Button>
          <Button
            onClick={handleUpdateCustomer}
            variant="contained"
            disabled={isSubmitting}
            size="large"
            startIcon={
              isSubmitting ? <CircularProgress size={20} /> : <EditIcon />
            }
          >
            {isSubmitting ? "جاري التحديث..." : "تحديث العميل"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف العميل "{selectedCustomer?.name}"؟
            <br />
            <strong>هذا الإجراء لا يمكن التراجع عنه.</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialogOpen(false)}
            disabled={isSubmitting}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={20} /> : <DeleteIcon />
            }
          >
            {isSubmitting ? "جاري الحذف..." : "حذف"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersPage;
